import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { type ChannelNamespace, MISSION_STATE_NAMESPACE, VEHICLE_TELEMETRY_NAMESPACE } from 'events/channels';
import { subscribeToFleetChannel } from 'events/eventSubscriptions';

import type { GenericRecord } from './utils';

/** The only place a mapping onto the GraphQL models has to be plugged in. */
type VehicleTelemetryEvent = { vin: string };
type MissionStateEvent = { vin: string };

const parseVehicleTelemetry = (event: unknown) => event as VehicleTelemetryEvent;
const parseMissionState = (event: unknown) => event as MissionStateEvent;

type Lifecycle<T extends { vin: string }> = {
    cacheDataLoaded: Promise<unknown>;
    cacheEntryRemoved: Promise<void>;
    updateCachedData: (recipe: (draft: GenericRecord<T>) => void) => void;
};

const streamFleetEvents = async <T extends { vin: string }>(
    namespace: ChannelNamespace,
    fleetId: string,
    label: string,
    parse: (event: unknown) => T,
    { cacheDataLoaded, cacheEntryRemoved, updateCachedData }: Lifecycle<T>
) => {
    await cacheDataLoaded;

    try {
        const connection = await subscribeToFleetChannel(namespace, fleetId, {
            next: event => {
                const parsed = parse(event);
                if (!parsed.vin) {
                    console.error(`[${label}]: event carries no vin`, event);
                    return;
                }
                updateCachedData(draft => {
                    draft[parsed.vin] = parsed;
                });
            },
            error: error => console.warn(error),
        });

        await cacheEntryRemoved;
        connection.unsubscribe();
    } catch (error) {
        console.error(`[${label}]: subscription to ${namespace} for fleet ${fleetId} failed`, error);
    }
};

export const realtimeEventsApi = createApi({
    reducerPath: 'realtimeEvents',
    baseQuery: fakeBaseQuery(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        onVehicleTelemetrySubscription: builder.query<GenericRecord<VehicleTelemetryEvent>, string>({
            queryFn: () => ({ data: {} }),
            onCacheEntryAdded: (fleetId, lifecycle) =>
                streamFleetEvents(
                    VEHICLE_TELEMETRY_NAMESPACE,
                    fleetId,
                    'onVehicleTelemetrySubscription',
                    parseVehicleTelemetry,
                    lifecycle
                ),
        }),
        onMissionStateSubscription: builder.query<GenericRecord<MissionStateEvent>, string>({
            queryFn: () => ({ data: {} }),
            onCacheEntryAdded: (fleetId, lifecycle) =>
                streamFleetEvents(
                    MISSION_STATE_NAMESPACE,
                    fleetId,
                    'onMissionStateSubscription',
                    parseMissionState,
                    lifecycle
                ),
        }),
    }),
});
